import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import { getSetting, loadAlerts, saveSetting, type Alert } from "../data";
import { Reminders } from "../components/Reminders";
import type { Workspace } from "../types";

const tone: Record<Alert["kind"], string> = {
  contract_end: "warning", probation_end: "warning", document_expiring: "warning", document_expired: "danger",
  documents_overdue: "danger", birthday: "info",
};
const KINDS: Alert["kind"][] = ["contract_end", "probation_end", "document_expiring", "document_expired", "documents_overdue", "birthday"];

/** Every upcoming date for your people, filterable, plus your own reminders and the reminder windows. */
export function Alerts({ ws, onOpen, onGo }: { ws: Workspace; onOpen: (id: string) => void; onGo: (page: string) => void }) {
  const { t, pick } = useI18n();
  const seesPeople = ws.permissions.has("employees.read") || ws.teamSize > 0;
  const [rows, setRows] = useState<Alert[] | null>(null);
  const [kind, setKind] = useState<Alert["kind"] | "all">("all");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => loadAlerts(ws.membership.company_id).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { if (seesPeople) void refresh(); }, [ws.membership.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const when = (n: number) => (n === 0 ? t("today") : n > 0 ? t("inDays").replace("{n}", String(n)) : t("daysAgo").replace("{n}", String(-n)));
  const count = (k: Alert["kind"]) => (rows ?? []).filter((r) => r.kind === k).length;
  const shown = (rows ?? []).filter((r) => kind === "all" || r.kind === kind);

  return (
    <div className="page">
      <Reminders ws={ws} onGo={onGo} empty={!seesPeople} />
      {seesPeople && (
        <section className="card">
          <h2>{t("alertsPeople")}</h2>
          {error && <p className="alert danger" role="alert">{error}</p>}
          <div className="filter-chips" role="group" aria-label={t("alertsFilter")}>
            <button type="button" className={`chip${kind === "all" ? " current" : ""}`} aria-pressed={kind === "all"} onClick={() => setKind("all")}>
              {t("alertsAll")} · {rows?.length ?? 0}
            </button>
            {KINDS.filter((k) => count(k) > 0).map((k) => (
              <button key={k} type="button" className={`chip${kind === k ? " current" : ""}`} aria-pressed={kind === k} onClick={() => setKind(k)}>
                {t(`alert_${k}` as StringKey)} · {count(k)}
              </button>
            ))}
          </div>
          {rows === null ? <p className="muted">{t("loading")}</p> : shown.length === 0 ? <p className="muted">{t("alertsNone")}</p> : (
            <ul className="alert-list">
              {shown.map((a) => (
                <li key={`${a.kind}-${a.employment_id}-${a.due_date}-${a.detail}`}>
                  <span className={`badge ${tone[a.kind]}`}>{t(`alert_${a.kind}` as StringKey)}</span>
                  <button type="button" className="link" onClick={() => onOpen(a.employment_id)}>{pick(a.full_name, a.full_name_ar)}</button>
                  {a.detail && <span className="muted">{a.kind === "documents_overdue" ? `${a.detail} ${t("alertsDocsCount")}` : a.detail}</span>}
                  <span className="mono muted alert-when">{a.due_date} · {when(a.days_left)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {ws.permissions.has("settings.manage") && <AlertSettings ws={ws} onSaved={refresh} />}
    </div>
  );
}

function AlertSettings({ ws, onSaved }: { ws: Workspace; onSaved: () => void }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const [f, setF] = useState({ contract: 30, probation: 14, document: 30, birthdays: true });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    void Promise.all(["alerts.contract_expiry_days", "alerts.probation_end_days", "alerts.document_expiry_days", "alerts.birthdays"].map((k) => getSetting(cid, k)))
      .then(([c, p, d, b]) => setF({ contract: Number(c ?? 30), probation: Number(p ?? 14), document: Number(d ?? 30), birthdays: b !== false }));
  }, [cid]);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await saveSetting(cid, "alerts.contract_expiry_days", f.contract);
      await saveSetting(cid, "alerts.probation_end_days", f.probation);
      await saveSetting(cid, "alerts.document_expiry_days", f.document);
      await saveSetting(cid, "alerts.birthdays", f.birthdays);
      setMsg({ ok: true, text: t("saved") });
      onSaved();
    } catch (err) { setMsg({ ok: false, text: err instanceof Error ? err.message : t("errorGeneric") }); }
  }
  const num = (k: "contract" | "probation" | "document", label: StringKey) => (
    <label className="field"><span>{t(label)}</span>
      <input type="number" min={1} max={365} value={f[k]} onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })} required /></label>
  );
  return (
    <form className="card" onSubmit={save}>
      <h2>{t("alertsSettings")}</h2>
      <div className="field-row">{num("contract", "alertsContractDays")}{num("probation", "alertsProbationDays")}{num("document", "alertsDocumentDays")}</div>
      <label className="check"><input type="checkbox" checked={f.birthdays} onChange={(e) => setF({ ...f, birthdays: e.target.checked })} /> {t("alertsBirthdays")}</label>
      <p className="muted small-note">{t("alertsInAppOnly")}</p>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn secondary">{t("save")}</button></div>
    </form>
  );
}
